import React, { useState, useEffect } from 'react';
import { autoReplyAPI } from '../lib/api';
import { 
  MessageSquare, CheckCircle, AlertTriangle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const AutoReplySettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await autoReplyAPI.getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      await autoReplyAPI.updateSettings({ enabled });
      setSettings({ ...settings, enabled });
      toast.success(enabled ? 'Auto-reply enabled!' : 'Auto-reply disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      await autoReplyAPI.generateNow();
      toast.success('Reply generated and saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate reply');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!settings?.can_use_auto_reply) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#FEF7E0] rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-[#F9AB00]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Auto-Reply Settings
            </h1>
            <p className="text-[#5F6368]">Automatically reply to customer reviews</p>
          </div>
        </div>

        <div className="dashboard-card text-center py-12">
          <AlertTriangle className="w-16 h-16 mx-auto text-[#F9AB00]" />
          <h2 className="mt-4 text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Upgrade to Unlock Auto-Reply
          </h2>
          <p className="mt-2 text-[#5F6368] max-w-md mx-auto">
            Auto-reply is available on Basic and Pro plans. Upgrade to automatically generate professional responses to customer reviews.
          </p>
          <Link to="/pricing" className="btn-google inline-block mt-6" data-testid="upgrade-plan-btn">
            View Plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#E6F4EA] rounded-xl flex items-center justify-center">
          <MessageSquare className="w-6 h-6 text-[#1E8E3E]" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Auto-Reply Settings
          </h1>
          <p className="text-[#5F6368]">
            {settings.replies_remaining_month} replies remaining this month
          </p>
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              settings.enabled ? 'bg-[#E6F4EA]' : 'bg-[#F1F3F4]'
            }`}>
              {settings.enabled ? (
                <CheckCircle className="w-6 h-6 text-[#1E8E3E]" />
              ) : (
                <MessageSquare className="w-6 h-6 text-[#5F6368]" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-[#202124]">Auto-Reply</h3>
              <p className="text-sm text-[#5F6368]">
                {settings.enabled 
                  ? 'Replies will be automatically generated for new reviews' 
                  : 'Enable to start auto-replying to reviews'}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle(!settings.enabled)}
            disabled={saving}
            className={`w-14 h-7 rounded-full transition-colors ${
              settings.enabled ? 'bg-[#1E8E3E]' : 'bg-[#DADCE0]'
            }`}
            data-testid="auto-reply-toggle"
          >
            <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform ${
              settings.enabled ? 'translate-x-7' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="dashboard-card">
        <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
          How Auto-Reply Works
        </h3>
        <div className="space-y-3">
          <div className="flex gap-3 p-3 bg-[#F8F9FA] rounded-lg">
            <div className="w-8 h-8 bg-[#E8F0FE] rounded-full flex items-center justify-center text-sm font-semibold text-[#1A73E8] shrink-0">1</div>
            <div>
              <p className="font-medium text-[#202124] text-sm">New review detected</p>
              <p className="text-xs text-[#5F6368]">When a customer posts a review on your GMB profile</p>
            </div>
          </div>
          <div className="flex gap-3 p-3 bg-[#F8F9FA] rounded-lg">
            <div className="w-8 h-8 bg-[#E8F0FE] rounded-full flex items-center justify-center text-sm font-semibold text-[#1A73E8] shrink-0">2</div>
            <div>
              <p className="font-medium text-[#202124] text-sm">AI analyzes sentiment</p>
              <p className="text-xs text-[#5F6368]">Our AI detects if the review is positive, neutral, or negative</p>
            </div>
          </div>
          <div className="flex gap-3 p-3 bg-[#F8F9FA] rounded-lg">
            <div className="w-8 h-8 bg-[#E8F0FE] rounded-full flex items-center justify-center text-sm font-semibold text-[#1A73E8] shrink-0">3</div>
            <div>
              <p className="font-medium text-[#202124] text-sm">Professional reply generated</p>
              <p className="text-xs text-[#5F6368]">A tailored, professional response is crafted and saved for your approval</p>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Generate */}
      <div className="dashboard-card">
        <h3 className="text-lg font-semibold text-[#202124] mb-2" style={{ fontFamily: 'Outfit' }}>
          Test Auto-Reply
        </h3>
        <p className="text-sm text-[#5F6368] mb-4">
          Manually trigger a reply generation to test the feature.
        </p>
        <button
          onClick={generateNow}
          disabled={generating}
          className="btn-google flex items-center gap-2"
          data-testid="generate-reply-now-btn"
        >
          {generating ? (
            <>
              <Sparkles className="w-5 h-5 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Generate Reply Now
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AutoReplySettingsPage;
